import React, {useEffect, useRef, useState} from 'react';
import styles from './index.module.scss'
import AttachmentService from "../../../../../../services/attachment.service";
import {
    fetchAttachmentObjectUrl,
    fetchAttachmentPreviewObjectUrl,
    fetchPublicAttachmentObjectUrl,
    isImg,
} from "../../../../../../helpers/attachmentImage";
import { getAttachmentUploadErrorMessage, getAttachmentValidationMessage } from '../../../../../../utils/attachmentValidation';
const ThinCheckIcon = () => (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const ThinCloseIcon = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
)

const attachmentPreviewUrls = new WeakMap<object, string>()

const revokeAttachmentPreviewUrl = (file: any) => {
    if (!file || typeof file !== 'object') return

    const objectUrl = attachmentPreviewUrls.get(file)

    if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        attachmentPreviewUrls.delete(file)
    }
}

const setAttachmentPreviewUrl = (file: any, objectUrl: string) => {
    revokeAttachmentPreviewUrl(file)
    file.src = objectUrl

    if (objectUrl.startsWith('blob:')) {
        attachmentPreviewUrls.set(file, objectUrl)
    }
}

interface AttachmentProps {
    taskId?: number
    attachments: any[]
    setAttachments: (next: any[]) => void
    onError?: (message: string | null) => void
    onActivityChange?: () => void
    pendingAttachmentsCount?: number
}

export default function Attachment({taskId, attachments, setAttachments, onError, onActivityChange, pendingAttachmentsCount = 0}: AttachmentProps) {
    const didLoadRef = useRef(false)
    const uploadingRef = useRef(false)
    const attachmentsRef = useRef(attachments)
    attachmentsRef.current = attachments
    const [uploading, setUploading] = useState(false)

    const setSrcToImage = async (attachmentList: any[]) => {
        for (let i = 0; i < attachmentList.length; i++) {
            const file = attachmentList[i];

            if (file.filename && isImg(file.filename)) {
                // Try the authorized endpoint first: it serves the file bytes
                // directly, while the public/signed URL can answer 200 with an
                // error page that would render as a broken image. The list
                // response does not always carry `file_url`, so fall back to
                // the canonical per-attachment path.
                const fileUrl = file.file_url
                    || (file.id ? `/api/attachments/${file.id}/file` : null)
                const objectUrl = await fetchAttachmentObjectUrl(fileUrl)

                if (objectUrl) {
                    setAttachmentPreviewUrl(file, objectUrl)
                    continue
                }

                let publicFileUrl: string | null = null

                try {
                    const data = file.id ? await AttachmentService.publicUrl(file.id) : null
                    publicFileUrl = data?.file_url || null
                } catch (error) {
                    console.error('Failed to get public image preview:', error)
                }

                if (publicFileUrl) {
                    const publicObjectUrl = await fetchPublicAttachmentObjectUrl(publicFileUrl)

                    if (publicObjectUrl) {
                        setAttachmentPreviewUrl(file, publicObjectUrl)
                    }
                }

                continue;
            }

            if (file instanceof File) {
                // A freshly picked File carries a real MIME type, so trust it
                // rather than matching the name against image extensions.
                if (file.type.startsWith('image/')) {
                    setAttachmentPreviewUrl(file, URL.createObjectURL(file))
                }

                attachmentList[i].filename = file.name;
                attachmentList[i].uploaded_at = file.lastModified;
            }
        }

        return attachmentList
    }

    const uploadFiles = async (e: any) => {
        const input = e.target as HTMLInputElement
        const files = Array.from(input.files || []) as File[]
        const validationError = getAttachmentValidationMessage(files, attachmentsRef.current.length + pendingAttachmentsCount)

        input.value = ''

        if (!files.length || uploadingRef.current) return

        if (validationError) {
            onError?.(validationError)
            return
        }

        onError?.(null)
        uploadingRef.current = true
        setUploading(true)

        try {
            if (taskId) {
                const results = await Promise.allSettled(
                    files.map((file) => AttachmentService.upload(taskId, file))
                )
                const uploaded = results.flatMap((result) => result.status === 'fulfilled' ? [result.value] : [])
                if (uploaded.length) {
                    const prepared = await setSrcToImage(uploaded)
                    setAttachments([...attachmentsRef.current, ...prepared])
                    onActivityChange?.()
                }
                const errors = results.flatMap((result) => result.status === 'rejected'
                    ? [getAttachmentUploadErrorMessage(result.reason)] : [])
                if (errors.length) onError?.(errors.join('\n'))
                return
            }

            const prepared = await setSrcToImage(files)
            setAttachments([...attachmentsRef.current, ...prepared])
        } catch (error) {
            onError?.(getAttachmentUploadErrorMessage(error))
        } finally {
            uploadingRef.current = false
            setUploading(false)
        }
    }

    const getFiles = async () => {
        if (!taskId || didLoadRef.current) {
            return
        }
        didLoadRef.current = true

        const attachmentList = await AttachmentService.list(taskId);
        const prepared = await setSrcToImage(attachmentList)
        setAttachments(prepared)
    }

    useEffect(() => {
        void getFiles()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskId])

    return (
        <div className={styles.attachment}>
            <div className={styles.attachment__icon}>
                <label htmlFor="file">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M10.7129 20.5C9.23956 20.5 7.98581 19.9829 6.95164 18.9487C5.91747 17.9146 5.40039 16.6608 5.40039 15.1875V7.325C5.40039 6.2625 5.77241 5.35952 6.51644 4.61605C7.26047 3.87258 8.16346 3.50057 9.22539 3.5C10.2873 3.49943 11.1906 3.87145 11.9352 4.61605C12.6798 5.36065 13.0515 6.26363 13.0504 7.325V12.85C13.0504 13.0908 12.9688 13.2928 12.8056 13.456C12.6424 13.6192 12.4407 13.7006 12.2004 13.7C11.9601 13.6994 11.7584 13.6178 11.5952 13.4552C11.432 13.2926 11.3504 13.0908 11.3504 12.85V7.325C11.3362 6.73 11.1274 6.22708 10.7239 5.81625C10.3205 5.40542 9.82096 5.2 9.22539 5.2C8.62982 5.2 8.12691 5.40542 7.71664 5.81625C7.30637 6.22708 7.10096 6.73 7.10039 7.325V15.1875C7.08622 16.1933 7.43331 17.047 8.14164 17.7485C8.84997 18.4501 9.70706 18.8006 10.7129 18.8C10.9537 18.8 11.1804 18.7751 11.3929 18.7252C11.6054 18.6753 11.8108 18.6153 12.0091 18.545C12.2358 18.46 12.4554 18.4637 12.6679 18.556C12.8804 18.6484 13.0291 18.8076 13.1141 19.0337C13.1991 19.2598 13.1957 19.4794 13.1039 19.6925C13.0121 19.9056 12.8526 20.0543 12.6254 20.1387C12.3279 20.2521 12.0199 20.3408 11.7014 20.4048C11.383 20.4688 11.0535 20.5006 10.7129 20.5ZM15.1754 19.65C14.9346 19.65 14.7328 19.5684 14.5702 19.4052C14.4076 19.242 14.326 19.0403 14.3254 18.8V17.1H12.6254C12.3846 17.1 12.1828 17.0184 12.0202 16.8552C11.8576 16.692 11.776 16.4903 11.7754 16.25C11.7748 16.0097 11.8564 15.808 12.0202 15.6448C12.184 15.4816 12.3857 15.4 12.6254 15.4H14.3254V13.7C14.3254 13.4592 14.407 13.2574 14.5702 13.0948C14.7334 12.9322 14.9351 12.8506 15.1754 12.85C15.4157 12.8494 15.6177 12.931 15.7814 13.0948C15.9452 13.2586 16.0265 13.4603 16.0254 13.7V15.4H17.7254C17.9662 15.4 18.1682 15.4816 18.3314 15.6448C18.4946 15.808 18.576 16.0097 18.5754 16.25C18.5748 16.4903 18.4932 16.6923 18.3306 16.856C18.168 17.0198 17.9662 17.1011 17.7254 17.1H16.0254V18.8C16.0254 19.0408 15.9438 19.2428 15.7806 19.406C15.6174 19.5692 15.4157 19.6506 15.1754 19.65ZM9.22539 16.25C8.98456 16.25 8.78282 16.1684 8.62019 16.0052C8.45756 15.842 8.37596 15.6403 8.37539 15.4V7.75C8.37539 7.50917 8.45699 7.30743 8.62019 7.1448C8.78339 6.98217 8.98512 6.90057 9.22539 6.9C9.46566 6.89943 9.66767 6.98103 9.83144 7.1448C9.99521 7.30857 10.0765 7.5103 10.0754 7.75V15.4C10.0754 15.6408 9.99379 15.8428 9.83059 16.006C9.66739 16.1692 9.46566 16.2506 9.22539 16.25ZM15.1754 11.15C14.9346 11.15 14.7328 11.0684 14.5702 10.9052C14.4076 10.742 14.326 10.5403 14.3254 10.3V7.75C14.3254 7.50917 14.407 7.30743 14.5702 7.1448C14.7334 6.98217 14.9351 6.90057 15.1754 6.9C15.4157 6.89943 15.6177 6.98103 15.7814 7.1448C15.9452 7.30857 16.0265 7.5103 16.0254 7.75V10.3C16.0254 10.5408 15.9438 10.7428 15.7806 10.906C15.6174 11.0692 15.4157 11.1506 15.1754 11.15Z"
                            fill="#64617E"/>
                    </svg>
                </label>
                <input
                    id={'file'}
                    type="file"
                    disabled={uploading}
                    hidden={true}
                    multiple={true}
                    onChange={uploadFiles}
                />
            </div>
        </div>
    )
}

interface AttachmentListProps {
    attachments: any[]
    setAttachments: (next: any[]) => void
    taskId?: number
    headerAction?: React.ReactNode
    onActivityChange?: () => void
}

const getFileLabel = (file: any) => file?.filename || file?.name || 'Файл'

const retryImagePreview = async (
    event: React.SyntheticEvent<HTMLImageElement>,
    file: any,
) => {
    const image = event.currentTarget

    if (image.dataset.previewRetried === 'true' || !file?.id) return
    image.dataset.previewRetried = 'true'

    try {
        const data = await AttachmentService.publicUrl(file.id)
        const publicFileUrl = data?.file_url || data?.url || data?.data?.file_url
        const objectUrl = await fetchPublicAttachmentObjectUrl(publicFileUrl)

        if (objectUrl) {
            setAttachmentPreviewUrl(file, objectUrl)
            image.src = objectUrl
        }
    } catch (error) {
        console.error('Failed to retry image preview:', error)
    }
}

const openFile = async (file: any) => {
    const previewWindow = window.open('', '_blank')

    if (previewWindow) {
        previewWindow.opener = null
    }

    let objectUrl: string | null = null

    if (file?.file_url) {
        objectUrl = await fetchAttachmentPreviewObjectUrl(file.file_url, getFileLabel(file))
    } else if (file instanceof Blob) {
        objectUrl = URL.createObjectURL(file)
    } else if (file?.src) {
        objectUrl = file.src
    }

    if (objectUrl) {
        if (previewWindow) {
            previewWindow.location.replace(objectUrl)
        } else {
            window.open(objectUrl, '_blank', 'noopener')
        }

        if (objectUrl !== file?.src && objectUrl.startsWith('blob:')) {
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
        }

        return
    }

    try {
        const data = file?.id ? await AttachmentService.publicUrl(file.id) : null
        const href = data?.file_url

        if (href && previewWindow) {
            previewWindow.location.replace(href)
        } else if (href) {
            window.open(href, '_blank', 'noopener')
        } else {
            previewWindow?.close()
        }
    } catch (error) {
        previewWindow?.close()
        console.error('Failed to open attachment:', error)
    }
}

const formatUploadedAt = (value: any) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yy = String(date.getFullYear()).slice(-2)
    return `${dd}/${mm}/${yy}`
}

export function AttachmentList({attachments, setAttachments, taskId, headerAction, onActivityChange}: AttachmentListProps) {
    const isEditMode = !!taskId
    const attachmentsRef = useRef(attachments)

    attachmentsRef.current = attachments

    useEffect(() => () => {
        attachmentsRef.current.forEach(revokeAttachmentPreviewUrl)
    }, [])

    if (!isEditMode && !attachments?.length) return null

    const titleNode = (
        <div className={styles.attachmentList__header}>
            <div className={styles.attachmentList__title}>Вложения</div>
            {headerAction && <div className={styles.attachmentList__headerAction}>{headerAction}</div>}
        </div>
    )

    const handleRemove = async (idx: number) => {
        const file = attachments[idx]
        const next = attachments.filter((_, i) => i !== idx)
        revokeAttachmentPreviewUrl(file)
        setAttachments(next)

        if (taskId && file?.id) {
            try {
                await AttachmentService.delete(file.id)
                onActivityChange?.()
            } catch (error) {
                console.error('Failed to delete attachment:', error)
            }
        }
    }

    const imageFiles = attachments.filter((file: any) => !!file?.src)
    const otherFiles = attachments.filter((file: any) => !file?.src)

    if (isEditMode) {
        return (
            <div className={styles.attachmentList}>
                {titleNode}
                {imageFiles.length > 0 && (
                    <div className={styles.attachmentList__grid}>
                        {imageFiles.map((file: any, idx: number) => (
                            <div
                                key={file?.id || `${getFileLabel(file)}-img-${idx}`}
                                className={styles.attachmentList__card}
                                onClick={() => openFile(file)}
                            >
                                <div className={styles.attachmentList__cardImg}>
                                    <img
                                        key={`attachment-preview-v2-${file?.id || idx}`}
                                        src={file.src}
                                        alt={getFileLabel(file)}
                                        onError={(event) => retryImagePreview(event, file)}
                                    />
                                </div>
                                <div className={styles.attachmentList__cardBottom}>
                                    <div className={styles.attachmentList__cardMeta}>
                                        <div className={styles.attachmentList__cardName} title={getFileLabel(file)}>
                                            {getFileLabel(file)}
                                        </div>
                                        {file.uploaded_at && (
                                            <div className={styles.attachmentList__cardDate}>{formatUploadedAt(file.uploaded_at)}</div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.attachmentList__remove}
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            const realIdx = attachments.indexOf(file)
                                            if (realIdx !== -1) handleRemove(realIdx)
                                        }}
                                    >
                                        <ThinCloseIcon />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {otherFiles.length > 0 && (
                    <div className={styles.attachmentList__items}>
                        {otherFiles.map((file: any, idx: number) => (
                            <div
                                key={file?.id || `${getFileLabel(file)}-${idx}`}
                                className={styles.attachmentList__item}
                                onClick={() => openFile(file)}
                            >
                                <div className={styles.attachmentList__check}>
                                    <ThinCheckIcon />
                                </div>
                                <div className={styles.attachmentList__name} title={getFileLabel(file)}>
                                    {getFileLabel(file)}
                                </div>
                                <button
                                    type="button"
                                    className={styles.attachmentList__remove}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        const realIdx = attachments.indexOf(file)
                                        if (realIdx !== -1) handleRemove(realIdx)
                                    }}
                                >
                                    <ThinCloseIcon />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className={styles.attachmentList}>
            {titleNode}
            <div className={styles.attachmentList__items}>
                {attachments.map((file: any, idx: number) => (
                    <div
                        key={file?.id || `${getFileLabel(file)}-${idx}`}
                        className={styles.attachmentList__item}
                        onClick={() => openFile(file)}
                    >
                        <div className={styles.attachmentList__check}>
                            <ThinCheckIcon />
                        </div>
                        <div className={styles.attachmentList__name} title={getFileLabel(file)}>
                            {getFileLabel(file)}
                        </div>
                        <button
                            type="button"
                            className={styles.attachmentList__remove}
                            onClick={(event) => {
                                event.stopPropagation()
                                handleRemove(idx)
                            }}
                        >
                            <ThinCloseIcon />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    )
}
